import { imageFromEnv } from "@p0security/integrations-gcloud-shared/mcp/servers";
import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import type { McpServerSpec } from "@p0security/shared/types/agentic/mcp";

import { TableauIconUrl } from "./icon.ts";
import { tableauMcpTools } from "./mcp-tools.ts";
import type { TableauAiIntegration } from "./types.ts";

const prefixArgs = ["tableau-ai", "permission"];

const accessPrompt = `Request access to Tableau projects by calling with argv:

  [<site>, <project>..., "--reason", <reason>]

where:

  - site: The Tableau site, as installed in P0
  - project: One or more project grants, each '<level>:<project id>', where
    level is 'read' to view content, or 'write' to also publish and edit it.
    Use the list tool to enumerate them.
  - reason: The reason access is needed

Every tool is listed whether or not your access covers it; Tableau refuses a
call for content that your projects do not include.
`;

/** tableau-mcp, signed in as the grant's agent user.
 *
 * The P0-built `tableau-mcp` image, because tableau-mcp takes a session token
 * only from an `X-Tableau-Auth` header. The image reads the token from its
 * environment and adds that header.
 */
export const tableauAiMcp: McpServerSpec & { type: "container" } = {
  type: "container",
  label: "Tableau",
  iconUrl: TableauIconUrl,
  credential: {
    type: "vault",
    // Each grant stages its own secret name; read that rather than rebuilding
    // the naming scheme here.
    path: "{{ request.generated.secretName }}",
  },
  // The agent user's project permissions decide what Tableau allows.
  authorization: "upstream",
  transport: "stdio",
  tools: { type: "manual", tools: tableauMcpTools },
  container: {
    command: [],
    args: [],
    envs: {
      SERVER: "{{ request.generated.server }}",
      SITE_NAME: "{{ request.generated.contentUrl }}",
      TABLEAU_SESSION_TOKEN: "{{ credential.token }}",
    },
    image: {
      image: imageFromEnv({
        env: "TABLEAU_MCP_IMAGE",
        fallback: "p0security/tableau-mcp",
      }),
    },
  },
  access: {
    prompt: accessPrompt,
    prefixArgs,
  },
  list: {
    fields: {
      site: {
        description: "Tableau site, as installed in P0",
        required: [],
      },
      policies: {
        description:
          "Project grant, '<level>:<project id>', for a project whose permissions are locked",
        required: ["site"],
      },
    },
    prefixArgs,
  },
  location: {
    key: "site",
    description: "Tableau site, as installed in P0",
  },
};

/** Whether {@link tableauAiMcp} can be added: only once a Tableau site is
 * installed, since without one there is nothing an agent could request. */
export const tableauAiMcpServerAvailability = {
  tableau: (installed: Record<string, NewConfig>): boolean =>
    installedIds(
      "credential",
      (installed["tableau-ai"] ?? {}) as TableauAiIntegration
    ).length > 0,
};
